import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { ScoringCriteriaModal } from '../ScoringCriteriaModal';
import { ThemeToggle } from '../ThemeToggle';
import { useAuth } from '../../context';
import { formatRoleLabel } from '../../utils/classificationLabels';
import {
  AnalyticsNavIcon,
  HubSyncNavIcon,
  LastFetchNavIcon,
  PatternsNavIcon,
  PipelineNavIcon,
} from '../navIcons';
import './AppShell.css';

const PromptsNavIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <path d="M14 2v6h6M8 13h8M8 17h5" />
  </svg>
);

const CriteriaNavIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 16v-4M12 8h.01" />
  </svg>
);

const LeaderboardNavIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M6 9H4.5a2.5 2.5 0 010-5H6M18 9h1.5a2.5 2.5 0 000-5H18" />
    <path d="M4 22h16" />
    <path d="M18 2H6v7a6 6 0 0012 0V2z" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

type NavItem = {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
  hidden?: boolean;
};

const WORK_NAV: NavItem[] = [
  { to: '/dashboard', label: 'Prompts', icon: <PromptsNavIcon />, end: true },
  // Screen code stays; hidden until Patterns is turned back on.
  { to: '/patterns', label: 'Patterns', icon: <PatternsNavIcon />, hidden: true },
];

const ScoreMixNavIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M21 12a9 9 0 11-9-9v9z" />
    <path d="M15 3.5A9 9 0 0120.5 9H15z" />
  </svg>
);

const RolesNavIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const ORG_NAV: NavItem[] = [
  // Screen code stays; hidden until Analytics and Score mix are turned back on.
  { to: '/analytics', label: 'Analytics', icon: <AnalyticsNavIcon />, end: true, hidden: true },
  { to: '/analytics/score-mix', label: 'Score mix', icon: <ScoreMixNavIcon />, hidden: true },
  { to: '/admin/roles', label: 'User roles', icon: <RolesNavIcon /> },
];

const MONITOR_NAV: NavItem[] = [
  { to: '/pipeline', label: 'Pipeline', icon: <PipelineNavIcon /> },
  { to: '/hub-sync-logs', label: 'Hub sync', icon: <HubSyncNavIcon /> },
  { to: '/last-fetch', label: 'Last fetch', icon: <LastFetchNavIcon /> },
];

export const AppShell: React.FC = () => {
  const { user, logout } = useAuth();
  const isInternalMonitor = user?.role === 'internal_monitor';
  const isLeadership = user?.role === 'leadership';
  const showMonitoring = isInternalMonitor || isLeadership;
  const [scoringModalOpen, setScoringModalOpen] = useState(false);

  const renderNavItem = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      className={({ isActive }) =>
        `app-shell__nav-item${isActive ? ' app-shell__nav-item--active' : ''}`
      }
      end={item.end}
    >
      <span className="app-shell__nav-icon">{item.icon}</span>
      <span className="app-shell__nav-label">{item.label}</span>
    </NavLink>
  );

  return (
    <div className="app-shell">
      <aside className="app-shell__sidebar" aria-label="Main navigation">
        <div className="app-shell__brand">
          <p className="app-shell__brand-title">Prompt Analysis <span>|</span> YorkIE</p>
        </div>

        <div className="app-shell__body">
          <nav className="app-shell__nav">
            {isLeadership && (
              <div className="app-shell__nav-section">
                <p className="app-shell__nav-section-label">Organization</p>
                <div className="app-shell__nav-section-items">
                  {ORG_NAV.filter((item) => !item.hidden).map(renderNavItem)}
                </div>
              </div>
            )}

            {showMonitoring && (
              <div className="app-shell__nav-section">
                <p className="app-shell__nav-section-label">Monitoring</p>
                <div className="app-shell__nav-section-items">
                  {MONITOR_NAV.map(renderNavItem)}
                </div>
              </div>
            )}

            <div className="app-shell__nav-section">
              <p className="app-shell__nav-section-label">General</p>
              <div className="app-shell__nav-section-items">
                {WORK_NAV.filter((item) => !item.hidden).map(renderNavItem)}
              </div>
            </div>
          </nav>

          <div className="app-shell__footer">
            <button
              type="button"
              className="app-shell__nav-item app-shell__nav-item--action"
              onClick={() => setScoringModalOpen(true)}
            >
              <span className="app-shell__nav-icon"><CriteriaNavIcon /></span>
              <span className="app-shell__nav-label">Scoring criteria</span>
            </button>

            {renderNavItem({
              to: '/leaderboard',
              label: 'Leaderboard',
              icon: <LeaderboardNavIcon />,
            })}

            {user && (
              <div className="app-shell__user-panel">
                <div className="app-shell__user-info">
                  <div className="app-shell__user-details">
                    <span className="app-shell__user-name">{user.name}</span>
                    <span className="app-shell__user-role">{formatRoleLabel(user.role)}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="app-shell__logout-btn"
                  onClick={logout}
                  aria-label="Log out"
                  title="Log out"
                >
                  <LogoutIcon />
                </button>
              </div>
            )}

            <ThemeToggle labeled />
          </div>
        </div>
      </aside>

      <div className="app-shell__main">
        <Outlet />
      </div>

      <ScoringCriteriaModal open={scoringModalOpen} onClose={() => setScoringModalOpen(false)} />
    </div>
  );
};

export default AppShell;
